import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useIntl } from 'react-intl';
import { BLOG_CATEGORY_PAGE, BLOG_HOME_PAGE, BLOG_PAGE } from '../../src/shared/pageTypes';
import { PAGES_MENU_DISPLAY_COUNT } from '../../constants/admin';
import { isDefaultLocale } from '../../modules/i18n/locales';
import useSearchablePages from '../hooks/useSearchablePages';
import BuilderPagesMenuItem, { BuilderPagesMenuItemLoading } from '../components/BuilderPagesMenuItem';
import PagesMenu, { PagesMenuLoading } from '../components/PagesMenu';

const GET_DATA = gql`
  query GetBuilderPagesMenuData(
    $excludeIds: [ID!],
    $first: Int!,
    $fulltext: String,
    $locale: String!,
    $nonDefaultLocale: Boolean!
    $excludeTypes: [String!]
  ) {
    pages(filter: {
      excludeIds: $excludeIds,
      fulltext: $fulltext,
      locale: $locale,
      excludeTypes: $excludeTypes
    }, first: $first) {
      edges {
        node {
          id
          ...BuilderPagesMenuItemPage
        }
      }
      totalCount
    }
  }

  ${BuilderPagesMenuItem.fragments.page}
`;

const propTypes = {
  className: PropTypes.string,
  excludeIds: PropTypes.arrayOf(PropTypes.string.isRequired),
  title: PropTypes.string,
};

const defaultProps = {
  className: undefined,
  excludeIds: [],
  title: undefined,
};

const BuilderPagesMenu = ({
  className,
  excludeIds,
  title,
  ...rest
}) => {
  const { locale } = useIntl();

  const [state, { onChange }] = useSearchablePages(
    GET_DATA,
    ({ fulltext }) => ({
      variables: {
        excludeIds,
        first: PAGES_MENU_DISPLAY_COUNT,
        fulltext,
        locale,
        nonDefaultLocale: !isDefaultLocale(locale),
        excludeTypes: [BLOG_HOME_PAGE, BLOG_CATEGORY_PAGE, BLOG_PAGE],
      },
      ssr: false,
    }),
    [locale, excludeIds],
  );

  const { error, loading, pages, queryCount, totalCount } = state;

  const enableSearch = totalCount > PAGES_MENU_DISPLAY_COUNT;

  if (!pages) {
    return (
      <PagesMenuLoading
        className={className}
        componentClass={BuilderPagesMenuItemLoading}
      />
    );
  }

  return (
    <PagesMenu
      {...rest}
      className={className}
      componentClass={BuilderPagesMenuItem}
      enableSearch={enableSearch}
      error={error}
      loading={loading}
      onSearch={onChange}
      pages={pages}
      title={title}
      totalPages={queryCount}
    />
  );
};

BuilderPagesMenu.propTypes = propTypes;
BuilderPagesMenu.defaultProps = defaultProps;

export default BuilderPagesMenu;
